<template>
  <v-container v-if="post" class="py-12">
    <!-- 顶部背景图 -->
    <div class="article-hero mb-12">
      <v-img
        src="https://source.unsplash.com/random/1920x600/?coffee-art"
        height="400"
        cover
        class="article-hero-image"
      >
        <div class="article-hero-overlay"></div>
        <div class="article-hero-content d-flex align-center justify-center">
          <v-icon icon="mdi-coffee" size="96" color="accent" class="mb-4"></v-icon>
        </div>
      </v-img>
    </div>

    <v-row>
      <v-col cols="12" lg="8" offset-lg="2">
        <!-- 返回按钮 -->
        <v-btn
          :to="{ name: 'Home' }"
          color="secondary"
          variant="text"
          class="mb-8"
          prepend-icon="mdi-arrow-left"
        >
          返回首页
        </v-btn>

        <!-- 文章内容 -->
        <article class="article-content">
          <div class="article-header text-center mb-12">
            <h1 class="text-h2 font-weight-bold mb-6" style="background: linear-gradient(45deg, var(--v-primary-base), var(--v-secondary-base)); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">
              {{ post.title }}
            </h1>
            <div class="d-flex justify-center align-center">
              <v-chip
                color="primary"
                variant="flat"
                class="mr-4"
                prepend-icon="mdi-calendar"
                size="large"
              >
                {{ post.date }}
              </v-chip>
              <v-chip
                color="secondary"
                variant="flat"
                prepend-icon="mdi-coffee"
                size="large"
              >
                {{ Math.ceil(post.content.length / 400) }} 分钟品读
              </v-chip>
            </div>
          </div>

          <div class="article-body text-body-1 text-grey-darken-3">
            {{ post.content }}
          </div>

          <div class="article-footer mt-12 pt-12 text-center">
            <v-icon icon="mdi-coffee-outline" size="32" color="accent" class="mb-4"></v-icon>
          </div>
        </article>

        <v-divider class="my-12"></v-divider>

        <!-- 评论区 -->
        <section class="comments-section">
          <h2 class="text-h4 font-weight-bold mb-8 text-center" style="background: linear-gradient(45deg, var(--v-secondary-base), var(--v-accent-base)); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">
            思想交流
          </h2>
          
          <!-- 评论表单 -->
          <v-card class="mb-12 comment-form" elevation="2">
            <v-card-text class="pa-6">
              <v-form @submit.prevent="submitComment">
                <v-text-field
                  v-model="newComment.author"
                  label="您的名字"
                  variant="outlined"
                  density="comfortable"
                  class="mb-6"
                  prepend-inner-icon="mdi-account"
                  required
                ></v-text-field>
                
                <v-textarea
                  v-model="newComment.content"
                  label="分享您的见解"
                  variant="outlined"
                  rows="4"
                  class="mb-6"
                  prepend-inner-icon="mdi-comment-text"
                  required
                ></v-textarea>

                <div class="d-flex justify-end">
                  <v-btn
                    type="submit"
                    color="primary"
                    size="large"
                    :disabled="!newComment.author || !newComment.content"
                    prepend-icon="mdi-send"
                    class="px-8"
                  >
                    发表评论
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- 评论列表 -->
          <div class="comments-list">
            <v-card
              v-for="comment in post.comments"
              :key="comment.id"
              class="mb-6 comment-card"
              elevation="1"
            >
              <v-card-item>
                <template v-slot:prepend>
                  <v-avatar
                    :color="getAvatarColor(comment.author)"
                    class="text-white elevation-2"
                  >
                    {{ comment.author.charAt(0).toUpperCase() }}
                  </v-avatar>
                </template>
                <v-card-title class="font-weight-bold">
                  {{ comment.author }}
                </v-card-title>
                <v-card-subtitle class="d-flex align-center">
                  <v-icon icon="mdi-clock-outline" size="small" class="mr-1"></v-icon>
                  {{ comment.date }}
                </v-card-subtitle>
              </v-card-item>

              <v-card-text class="text-grey-darken-2 pt-4 text-body-1">
                {{ comment.content }}
              </v-card-text>
            </v-card>
          </div>
        </section>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBlogStore } from '../stores/blog'

const route = useRoute()
const store = useBlogStore()
const { posts } = storeToRefs(store)

const post = computed(() => {
  const postId = Number(route.params.id)
  return posts.value.find(p => p.id === postId)
})

const newComment = ref({
  author: '',
  content: ''
})

const submitComment = () => {
  const postId = Number(route.params.id)
  store.addComment(postId, {
    author: newComment.value.author,
    content: newComment.value.content
  })
  newComment.value = {
    author: '',
    content: ''
  }
}

const colors = ['primary', 'secondary', 'accent', 'tertiary', 'quaternary']
const getAvatarColor = (name) => {
  const index = name.charCodeAt(0) % colors.length
  return colors[index]
}
</script>

<style scoped>
.article-hero {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 66, 107, 0.15);
}

.article-hero-image {
  transform: scale(1.02);
}

.article-hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 66, 107, 0.85), rgba(0, 177, 164, 0.85));
}

.article-hero-content {
  position: relative;
  z-index: 1;
  height: 100%;
}

.article-content {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  margin-top: -100px;
  background: white;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 4px 24px rgba(0, 66, 107, 0.1);
}

.article-header h1 {
  font-family: "Times New Roman", serif;
  letter-spacing: 0.5px;
}

.article-body {
  font-family: "Georgia", serif;
  line-height: 2;
  letter-spacing: 0.3px;
  font-size: 1.1rem;
  text-align: justify;
}

.article-body p {
  margin-bottom: 1.5rem;
}

.article-footer {
  border-top: 1px solid rgba(0, 66, 107, 0.1);
}

.comment-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 66, 107, 0.05);
  background: linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%);
}

.comment-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--v-secondary-base), var(--v-accent-base));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.comment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 66, 107, 0.1);
}

.comment-card:hover::before {
  opacity: 1;
}

.comment-form {
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%);
}

.comments-section {
  max-width: 800px;
  margin: 0 auto;
}

:deep(.v-field) {
  border-radius: 8px;
  font-family: "Georgia", serif;
}

:deep(.v-btn) {
  letter-spacing: 1px;
  text-transform: none;
  font-family: "Georgia", serif;
}

:deep(.v-chip) {
  font-family: "Times New Roman", serif;
  font-weight: 500;
  font-size: 1rem;
  padding: 0 16px;
}

:deep(.v-chip .v-icon) {
  font-size: 1.2rem;
}
</style> 